import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Grid, Divider, Button, Link, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';

import { API_URL } from "../config";

export default function CandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchCandidate() {
      setLoading(true);
      try {
        const res = await fetchWithAuth(`${API_URL}/candidates/${id}`);
        if (!res.ok) {
          if (res.status === 403) {
            setError('You are not authorized to perform this action. Please contact your system administrator.');
            return;
          }
          throw new Error('Failed to fetch candidate');
        }
        const data = await res.json();
        setCandidate(data);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    fetchCandidate();
  }, [id]);

  if (loading) return <Box p={4}><CircularProgress /></Box>;
  if (error) return <Box p={4}><Typography color="error">{error}</Typography></Box>;
  if (!candidate) return <Box p={4}><Typography>No candidate found.</Typography></Box>;

  const { first_name, last_name, email, phone, location, attachments, job_orders, activity, questions_answers } = candidate;

  return (
    <Box sx={{ p: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Candidate Details</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography><b>Name:</b> {first_name} {last_name}</Typography>
            <Typography><b>Email:</b> <a href={`mailto:${email}`}>{email}</a></Typography>
            <Typography><b>Phone:</b> {phone || <em>None</em>}</Typography>
            <Typography><b>Location:</b> {location || <em>None</em>}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Attachments:</Typography>
            {attachments && attachments.length > 0 ? attachments.map((att, i) => (
              <Button key={i} href={att.url} startIcon={<DownloadIcon />} target="_blank" rel="noopener" sx={{ mr: 1 }}>
                {att.name || `Attachment ${i+1}`}
              </Button>
            )) : <Typography variant="body2" color="text.secondary">No attachments.</Typography>}
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Job Orders:</Typography>
        {job_orders && job_orders.length > 0 ? (
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date Applied</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {job_orders.map((jo, idx) => (
                  <TableRow key={jo.id || idx}>
                    <TableCell>{jo.title}</TableCell>
                    <TableCell>{jo.company_name}</TableCell>
                    <TableCell><Chip label={jo.status} size="small" /></TableCell>
                    <TableCell>{jo.date_applied ? new Date(jo.date_applied).toLocaleString() : <em>Unknown</em>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : <Typography variant="body2" color="text.secondary">No job orders found.</Typography>}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Questions & Answers:</Typography>
        {questions_answers && questions_answers.length > 0 ? (
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Question</TableCell>
                  <TableCell>Answer</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {questions_answers.map((qa, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{qa.question}</TableCell>
                    <TableCell>{qa.answer}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : <Typography variant="body2" color="text.secondary">No questions answered.</Typography>}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Activity:</Typography>
        {activity && activity.length > 0 ? (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Regarding</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activity.map((act, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{act.date ? new Date(act.date).toLocaleString() : <em>Unknown</em>}</TableCell>
                    <TableCell>{act.type}</TableCell>
                    <TableCell>{act.regarding}</TableCell>
                    <TableCell>{act.notes}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : <Typography variant="body2" color="text.secondary">No activity found.</Typography>}
      </Paper>
      <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
    </Box>
  );
}
